import { useMedia, shortcut } from '../ui/responsive';
import { useEffect, useState } from 'react';
import { hexA } from '@scribbleplot/shared';
import { color, font, shadow } from '../tokens';
import { STEPS, useOnboarding } from './onboarding';

/**
 * Getting-started checklist, bottom-left of the canvas.
 *
 * Sits beside the tool rail rather than over the board, so it never covers the
 * thing it is telling you to do. Completing the last step holds the panel for a
 * beat before it leaves, so the final tick is actually seen.
 */
export function OnboardingPanel({
  accent,
  onComplete,
  onDismiss,
}: {
  accent: string;
  /** Called once, when every step is done — used to record it on the account. */
  onComplete: () => void;
  onDismiss: () => void;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const touch = useMedia('(pointer: coarse)');
  const active = useOnboarding((s) => s.active);
  const done = useOnboarding((s) => s.done);
  const finishing = useOnboarding((s) => s.finishing);
  const finish = useOnboarding((s) => s.finish);

  const complete = STEPS.every((step) => done[step.key]);

  useEffect(() => {
    if (!active || !complete) return;
    if (!finishing) finish();
    // Long enough to register the last tick, short enough not to linger.
    const timer = setTimeout(onComplete, 900);
    return () => clearTimeout(timer);
  }, [active, complete, finishing, finish, onComplete]);

  if (!active) return null;

  return (
    <div
      className="sp-onboarding"
      role="status"
      aria-label="Getting started"
      style={{
        position: 'absolute',
        left: 66,
        bottom: 14,
        width: 252,
        background: color.panel,
        border: `1px solid ${color.lineSoft}`,
        borderRadius: 14,
        zIndex: 50,
        boxShadow: shadow.float,
        padding: '13px 15px 11px',
        animation: 'sp-pop-in .18s ease',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <div style={{ fontSize: 10, fontFamily: font.mono, letterSpacing: '.12em', color: color.textFaint }}>
          <button className="sp-onboarding-toggle" aria-expanded={!collapsed} onClick={() => setCollapsed(!collapsed)}>
            Getting started {collapsed ? '＋' : '−'}
          </button>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          title="Dismiss"
          aria-label="Dismiss getting started"
          style={{
            width: 20,
            height: 20,
            border: 'none',
            background: 'transparent',
            color: color.textFaint,
            cursor: 'pointer',
            fontSize: 14,
            padding: 0,
            lineHeight: 1,
          }}
        >
          ×
        </button>
      </div>

      {!collapsed &&
        STEPS.map((step) => {
          const ticked = done[step.key];
          return (
            <div key={step.key} style={{ display: 'flex', gap: 9, alignItems: 'flex-start', padding: '5px 0' }}>
              <span
                aria-hidden
                style={{
                  width: 17,
                  height: 17,
                  borderRadius: '50%',
                  flex: 'none',
                  marginTop: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 10,
                  fontWeight: 700,
                  background: ticked ? accent : 'transparent',
                  color: ticked ? color.onAccent : 'transparent',
                  border: ticked ? 'none' : `1.5px solid ${hexA('#FFFFFF', 0.25)}`,
                  transition: 'background .15s',
                }}
              >
                ✓
              </span>
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: ticked ? color.textFaint : color.textStrong,
                    textDecoration: ticked ? 'line-through' : 'none',
                  }}
                >
                  {step.title}
                </div>
                <div style={{ fontSize: 11, color: color.textDim, marginTop: 1 }}>
                  {touch
                    ? {
                        note: 'Choose Note, then tap the canvas',
                        image: 'Choose Add → Add image',
                        group: 'Add → Select multiple, then Edit selection',
                        zoom: 'Pinch to zoom, two fingers to pan',
                      }[step.key]
                    : shortcut(step.how)}
                </div>
              </div>
            </div>
          );
        })}
    </div>
  );
}
